import React, { useCallback, useEffect, useRef, useState } from "react";
import { Backdrop, Button, Checkbox, CircularProgress, FormControlLabel, IconButton } from "@mui/material";
import { Box as MantineBox } from "@mantine/core";
import { useForm } from "@mantine/form";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import ShieldOutlinedIcon from "@mui/icons-material/ShieldOutlined";
import SupportAgentOutlinedIcon from "@mui/icons-material/SupportAgentOutlined";
import PublicOutlinedIcon from "@mui/icons-material/PublicOutlined";
import HeadsetMicOutlinedIcon from "@mui/icons-material/HeadsetMicOutlined";
import FingerprintOutlinedIcon from "@mui/icons-material/FingerprintOutlined";
import AccountCircleOutlinedIcon from "@mui/icons-material/AccountCircleOutlined";
import OpenInNewOutlinedIcon from "@mui/icons-material/OpenInNewOutlined";
import { useNavigate } from "react-router";
import { createRoot, Root } from "react-dom/client";
import { AxiosRequestConfig } from "axios";
import { useAtom, useAtomValue } from "jotai";
import clsx from "clsx";
import SelectDrx from "./SelectDrx";
import SelectUser from "./SelectUser";
import SelectWorkPlace from "./SelectWorkPlace";
import ModalSignIn from "./SignInWithCryptoPRO/ModalSignIn";
import ChooseCert from "./SignInWithCryptoPRO/ChooseCert";
import SignInEditor from "./signInTools/SignInEditor";
import ChangePassowrd from "../MainPage/NotWorkArea(Side&Head)/Header/ChangePassowrd";
import { checkGenLicGuid, createCokies, getURLparam, hasURLparam, updateOptionsForUser } from "../Url";
import { getUrlSearh } from "../MainPage/Tools/urlParams";
import { isEmptyObject, selectNewLicenseToInstall, selectOrg } from "../MainPage/Tools/Tools";
import { CheckModalBackGrounds, executeCertList, renderMessageBox } from "../TokenProcessing/TokenProcessing";
import { smetaModals } from "../MainPage/Tools/modalManager";
import { hideSnow } from "../MainPage/WrapperMainFile";
import smetaAxios from "../SmetaAxios";
import { appConfig, isAuthorized } from "../../atoms";
import { CheckWorkSpace, enterSmeta, getUserInfo, initContext, updateStatesAfterEnter } from "./SingIn";
import styles from "./signin.module.css";

interface SmetaEnterPostData {
  configName: string;
  userName: string;
  workPlace: string;
  password: string;
  RCDATA?: string;
  orgTypeCls?: string;
  drx: string;
}

interface SignInFormValues {
  drx: string;
  user: string;
  password: string;
  workplace: string;
}

const SmetaSignIn = () => {
  document.title = "Вход в систему";

  const config = useAtomValue(appConfig);
  const navigate = useNavigate();
  const [drx, setDrx] = useState("");
  const [hasOrgs, setHasOrgs] = useState("0");
  const [user, setUser] = useState("");
  const [workplace, setWorkPlace] = useState("");
  const [password, setPassword] = useState("");
  const [load, setLoad] = useState(false);
  const [secret, setSecret] = useState<any>({});
  const [display, setDisplay] = useState<string>("none");
  const [showPassword, setShowPassword] = useState(false);
  const [orgTypeCls, setOrgTypeCls] = useState<any>(null);
  const [isSessionActive, setIsSessionActive] = useAtom(isAuthorized);
  const lkButtonShow = useRef(
    config.smeta?.signOptions?.signButtons?.lkButton && !config.smeta?.signOptions?.splitSignIn,
  );
  const getConfigInfoTimeout = useRef<NodeJS.Timeout>(undefined);
  const textAboutEnter = config.smeta?.signOptions?.textAboutButtons?.smeta;
  const productTitle = config.smeta?.signOptions?.headerSettings?.title || "ЕСПК";
  const showHelpKrista = Boolean(config.smeta?.signOptions?.signButtons?.helpKristaButton);
  // Форма тут больше для нормального submit/enter. Значения все равно живут в старых селектах.
  const form = useForm<SignInFormValues>({
    initialValues: {
      drx: "",
      user: "",
      password: "",
      workplace: "",
    },
  });
  const hiddenAfterDividerButtons = {
    crypto: !config.smeta?.signOptions?.signButtons?.signCryptoButton,
    lk: !lkButtonShow.current,
  };
  const hideAfterDivider = Object.values(hiddenAfterDividerButtons).every(Boolean);

  useEffect(() => {
    if (isEmptyObject(secret) === false) {
      // После ЭП сюда прилетает готовый секрет, дальше уже обычный заход в main.
      const appName = secret["AppName"];
      createCokies("drx", appName === undefined ? drx : appName);
      setIsSessionActive(true);
      navigate("main");
    }
  }, [secret]);

  useEffect(() => {
    // Организацию держим привязанной к конфигурации, иначе легко войти не туда.
    setOrgTypeCls(null);
  }, [drx]);

  useEffect(() => {
    if (drx !== "") {
      // Не дергаем getConfigInfo на каждый символ, пользователи часто выбирают из списка.
      clearTimeout(getConfigInfoTimeout.current);
      getConfigInfoTimeout.current = setTimeout(getConfigInfo, 250, drx, user);
    }
  }, [drx, user]);

  useEffect(() => {
    hideSnow(true);
    // Если пришли после установки лицензии, старый вход сразу продолжал авторизацию.
    if (hasURLparam("Guid")) {
      sessionStorage.setItem("LicG", getURLparam("Guid"));
      createCokies("LicG", getURLparam("Guid"));
      handleSingIn();
    } else {
      checkSessionAndEnter().then((res) => {
        if (res === false) checkGenLicGuid(sessionStorage.getItem("LicG"), true);
      });
    }
    setDisplay(getStyleForEnter());
    return () => {
      setLoad(false);
    };
  }, []);

  const showLicenseMessage = (content: string) => {
    renderMessageBox({
      dlgType: 3,
      buttons: 12,
      message: `${content} Хотите выбрать новую лицензию для установки?`,
      selectF: async () => {
        // RCDATA подкладываем в обычный парольный вход, сервер дальше сам решает что с лицензией.
        const fileData = await selectNewLicenseToInstall();
        if (fileData && !isEmptyObject(fileData as Record<string, unknown>)) {
          const fileName = fileData.reqData.FileName;
          const rcdata = fileData.files[fileName].rcdata;
          handleSingIn({}, rcdata);
        }
      },
    });
  };

  async function checkSessionAndEnter() {
    const isActive = isSessionActive;
    if (isActive === true) {
      navigate(`main` + getUrlSearh());
    }
    return isActive;
  }

  function executeCertListAsXML() {
    // Вход по ЭП оставлен тем же маршрутом: список сертификатов, выбор, подпись, ответ сервера.
    executeCertList()
      .then((certList: string) => {
        smetaModals.open(<ChooseCert data={certList.split(/\r?\n/)} onSelect={signForEnterOnSelect} />, {
          Title: "Выбор сертификата",
          style: {
            height: `${530}px`,
            width: `${580}px`,
            overflow: "hidden",
          },
          hiddenButton: true,
          blockMaximize: true,
          blockResize: true,
        });
      })
      .catch(() => {
        if (CheckWorkSpace()) return;
      });
  }

  async function signForEnterOnSelect(sn: string) {
    const json: any = await smetaAxios.get("secret");
    const authSecret = json["Pkcs7Auth-Secret"].Secret;
    window.webkcrypt
      .make_pkcs7_signature(sn, authSecret, true)
      .value.then((value: any) => {
        handleMessage(removeLinebreaks(value), authSecret);
      })
      .catch((e: any) => {
        console.log(e);
      });
  }

  function removeLinebreaks(str: string) {
    let newstr = "";
    for (let i = 0; i < str.length; i++) if (!(str[i] == "\n" || str[i] == "\r")) newstr += str[i];
    return newstr;
  }

  function getStyleForEnter() {
    // Старое поведение: при скрытом входе или Guid экран вообще не рисуем.
    if (hasURLparam("Guid") || (config.smeta.hideEnter !== undefined && config.smeta.hideEnter === true)) {
      return "none";
    }
    return "";
  }

  async function getConfigInfo(drx: string, user: string) {
    const infoConfig: AxiosRequestConfig = {
      params: {
        configName: drx,
        userName: user || null,
      },
    };
    const resp: any = await smetaAxios.get("getConfigInfo", infoConfig);
    setHasOrgs(resp.HasOrgs === "1" ? "1" : "0");
  }

  const handleClickShowPassword = () => setShowPassword((show) => !show);

  const handleMouseDownPassword = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
  };

  function handleMessage(message: any, authSecret: any) {
    if (!message) return;
    const loginData = {
      "Pkcs7Auth-Answer": {
        Answer: message,
      },
    };
    const json = smetaAxios.post("answer", loginData);
    const content = <ModalSignIn data={json} secret={authSecret} keyFromSelect={message} setSecret={setSecret} />;
    const renderSignInEl = document.getElementById("renderSignIn") as HTMLElement & { root?: Root };
    const renderSignInElRoot: Root = renderSignInEl.root || createRoot(renderSignInEl);
    renderSignInEl.root = renderSignInElRoot;
    renderSignInElRoot.render(content);
  }

  const navigatiToMain = async (IP: string | null) => {
    // InitContext важен до перехода: часть настроек интерфейс забирает уже из options.
    const licG = sessionStorage.getItem("LicG");
    localStorage.setItem("login", licG || "");
    const initData: any = await initContext();
    if (IP) {
      updateOptionsForUser({ IP: IP });
    }
    if (initData.RefWithCtrl) {
      updateOptionsForUser({ RefWithCtrl: initData.RefWithCtrl === "1" });
    }
    setIsSessionActive(true);
    navigate("main" + getUrlSearh());
  };

  const clientPasswordHandler = (value: string) => {
    // Дублирование намеренное: старая логика читает state, form нужен для submit.
    setPassword(value);
    form.setFieldValue("password", value);
  };

  const setDrxHandler = (value: string) => {
    setDrx(value);
    form.setFieldValue("drx", value);
  };

  const setUserHandler = (value: string) => {
    setUser(value);
    form.setFieldValue("user", value);
  };

  const setWorkPlaceHandler = (value: string) => {
    setWorkPlace(value);
    form.setFieldValue("workplace", value);
  };

  function checkAnswerFromServer(resp?: any) {
    setLoad(false);
    const enterErrors = {
      Password: () => {
        if (resp.NeedNewPW !== "1") return;
        smetaModals.open(
          <ChangePassowrd
            where={"signin"}
            onPasswordChange={(newPassword: string) => {
              handleSingIn({ password: newPassword });
            }}
          />,
          {
            style: { height: window.innerHeight * 0.35, width: window.innerWidth * 0.3 },
            hiddenButton: true,
            Title: resp.content,
          },
        );
      },
    };
    enterErrors[resp.Item as keyof typeof enterErrors]?.();
    if (resp.LicRes) showLicenseMessage(resp.content);
  }

  const handleKeyDownSignIn = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === "Enter" && CheckModalBackGrounds() === 0) {
        e.preventDefault();
        handleSingIn();
      }
    },
    [drx, user, password, workplace, orgTypeCls],
  );

  const submitSignIn = () => {
    // Enter из формы и кнопка должны проходить через один и тот же фильтр модалок.
    if (CheckModalBackGrounds() === 0) {
      handleSingIn();
    }
  };

  const handleSingIn = async (loginObj = {}, rcdata?: string) => {
    const configName = orgTypeCls?.config || drx;
    const userInfo: any = await getUserInfo(configName, user);
    let ip: string;
    ip = userInfo.Server;
    if (hasURLparam("stimWebSrv") && getURLparam("stimWebSrv") !== undefined) {
      ip = getURLparam("stimWebSrv");
    }
    const loginData = {
      configName: orgTypeCls?.config || drx,
      userName: user,
      password: password,
      workPlace: workplace,
      RCDATA: rcdata,
      orgTypeCls: orgTypeCls?.code,
      drx: drx,
      ...loginObj,
    };
    if (!isEmptyObject(orgTypeCls)) {
      setLoad(true);
      axiosEnter(loginData, ip);
    } else if (hasOrgs === "1" && isEmptyObject(orgTypeCls)) {
      // У некоторых пользователей вход идет через организацию. Если она одна, не дергаем лишний выбор.
      if (userInfo.Configs && userInfo.Configs.items && userInfo.Configs.items.length === 1) {
        axiosEnter(
          {
            ...loginData,
            orgTypeCls: userInfo.Configs.items[0].Code,
            configName: userInfo.Configs.items[0].Config || drx,
          },
          ip,
        );
      } else {
        selectOrg({ drx, user }).then((resp) => {
          if (!resp) return;
          setOrgTypeCls(resp);
          setLoad(true);
          axiosEnter({ ...loginData, orgTypeCls: resp.code, configName: resp.config }, ip);
        });
      }
    } else {
      axiosEnter(loginData, ip);
    }
  };

  async function axiosEnter(loginData: SmetaEnterPostData, ip: string | null = null) {
    // Пустой ответ тут означает, что сервер просит пройти ветку с LicGuid.
    const resp: any = await enterSmeta(loginData, { ip });
    setLoad(false);
    if (isEmptyObject(resp as Record<string, unknown>)) return checkGenLicGuid("", true);
    if (resp.error) return checkAnswerFromServer(resp.error);
    updateStatesAfterEnter(resp, { ...loginData, drx });
    navigatiToMain(ip);
  }

  return (
    <div className={clsx(styles.smetaLoginPage)} style={{ display }}>
      <div className={clsx(styles.smetaLoginShell)}>
        <div className={clsx(styles.smetaLoginIntro)}>
          <div className={clsx(styles.smetaBrand)}>
            <div className={clsx(styles.smetaBrandMark)}>Σ</div>
            <div>
              <div className={clsx(styles.smetaBrandName)}>{productTitle}</div>
              <div className={clsx(styles.smetaBrandCaption)}>Единая система поддержки клиентов</div>
            </div>
          </div>

          <div className={clsx(styles.smetaWelcome)}>
            <div className={clsx(styles.smetaWelcomeTitle)}>Добро пожаловать!</div>
            <div className={clsx(styles.smetaWelcomeText)}>Войдите в систему для продолжения работы</div>
          </div>

          <div className={clsx(styles.smetaBenefits)}>
            <div className={clsx(styles.smetaBenefit)}>
              <ShieldOutlinedIcon />
              <div>
                <div className={clsx(styles.smetaBenefitTitle)}>Безопасность</div>
                <span className={clsx(styles.smetaBenefitHelpText)}>
                  Ваши данные надежно защищены и передаются по зашифрованному каналу
                </span>
              </div>
            </div>
            <div className={clsx(styles.smetaBenefit)}>
              <PublicOutlinedIcon />
              <div>
                <div className={clsx(styles.smetaBenefitTitle)}>Доступность</div>
                <span className={clsx(styles.smetaBenefitHelpText)}>
                  Доступ 24/7, без ограничений по часовым поясам
                </span>
              </div>
            </div>
            <div className={clsx(styles.smetaBenefit)}>
              <SupportAgentOutlinedIcon />
              <div>
                <div className={clsx(styles.smetaBenefitTitle)}>Поддержка</div>
                <span className={clsx(styles.smetaBenefitHelpText)}>
                  Наша команда всегда готова помочь и ответить на ваши вопросы
                </span>
              </div>
            </div>
          </div>

          {showHelpKrista && (
            <div className={clsx(styles.smetaHelpBox)}>
              <HeadsetMicOutlinedIcon />
              <div>
                <div className={clsx(styles.smetaBenefitTitle)}>Нужна помощь?</div>
                <span>Обратитесь в службу поддержки</span>
                <button type="button" onClick={() => window.open("https://help.krista.ru")}>
                  help.krista.ru <OpenInNewOutlinedIcon />
                </button>
              </div>
            </div>
          )}
        </div>

        <div className={clsx(styles.smetaSignInCard)}>
          <div className={clsx(styles.smetaCardHeader)}>
            <div className={clsx(styles.smetaCardTitle)}>Вход в систему</div>
            <div className={clsx(styles.smetaCardSubtitle)}>Введите свои данные для входа</div>
            {textAboutEnter && <div className={clsx(styles.smetaCardText)}>{textAboutEnter}</div>}
          </div>

          <MantineBox component="form" className={clsx(styles.smetaForm)} onSubmit={form.onSubmit(submitSignIn)}>
            <div className={clsx(styles.smetaField)}>
              <SelectDrx
                drxInfo={drx}
                setBackInfo={setDrxHandler}
                password={undefined}
                userInfo={undefined}
                KeyDown={handleKeyDownSignIn}
              />
            </div>

            <div className={clsx(styles.smetaField)}>
              <SelectUser
                HasOrgs={hasOrgs}
                userInfo={user}
                drxInfo={drx}
                setBackInfo={setUserHandler}
                password={undefined}
                selectOrg={() => {
                  selectOrg({ drx, user }).then((resp) => {
                    setOrgTypeCls(resp || orgTypeCls);
                  });
                }}
                KeyDown={handleSingIn}
                selectedOrg={orgTypeCls}
              />
            </div>

            <div className={clsx(styles.smetaField)}>
              <SignInEditor
                label="Пароль"
                type={showPassword ? "text" : "password"}
                autocomplete="password"
                onKeyDown={handleKeyDownSignIn}
                onEdit={clientPasswordHandler}
                endButtons={
                  <IconButton onClick={handleClickShowPassword} onMouseDown={handleMouseDownPassword} size="small">
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                }
              />
            </div>

            <div className={clsx(styles.smetaField)}>
              <SelectWorkPlace
                drxInfo={drx}
                userInfo={user}
                workPlaceInfo={workplace}
                setBackInfo={setWorkPlaceHandler}
                password={undefined}
                KeyDown={handleKeyDownSignIn}
              />
            </div>

            <div className={clsx(styles.smetaFormMeta)}>
              <FormControlLabel control={<Checkbox size="small" defaultChecked />} label="Запомнить меня" />
            </div>

            <Button
              className={clsx(styles.smetaPrimaryButton)}
              type="submit"
              fullWidth
              variant="contained"
              startIcon={<LockOutlinedIcon />}
            >
              Войти
            </Button>

            {!hideAfterDivider && (
              <div className={clsx(styles.smetaDivider)}>
                <span />
                <div className={clsx(styles.smetaDividerText)}>или войдите с помощью</div>
                <span />
              </div>
            )}

            <Button
              className={clsx(styles.smetaSecondaryButton)}
              fullWidth
              variant="outlined"
              startIcon={<FingerprintOutlinedIcon />}
              onClick={executeCertListAsXML}
              sx={{ display: hiddenAfterDividerButtons.crypto ? "none" : "inline-flex" }}
            >
              Вход с электронной подписью
            </Button>

            <Button
              className={clsx(styles.smetaSecondaryButton)}
              fullWidth
              variant="outlined"
              startIcon={<AccountCircleOutlinedIcon />}
              onClick={() => {
                navigate("/lk" + getUrlSearh());
              }}
              sx={{ display: hiddenAfterDividerButtons.lk ? "none" : "inline-flex" }}
            >
              Вход в Личный кабинет
            </Button>
          </MantineBox>

          <div className={clsx(styles.smetaCopyright)}>© 2026 НПО "Криста" Все права защищены</div>
          <div id="renderSignIn" />
        </div>
      </div>
      <Backdrop sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }} open={load}>
        <CircularProgress color="inherit" />
      </Backdrop>
    </div>
  );
};

export default SmetaSignIn;
